import * as React from 'react';
import { Search } from 'lucide-react';
import {
  SearchField,
  Sheet,
  SheetStat,
  TriCheckbox,
  WorkspaceButton,
  WorkspaceNotice,
} from '@/components/ui/workspace';
import { Highlight } from '@/components/requirement-library/highlight';
import { matchesSearch, searchTerms } from '@/components/requirement-library/library-logic';
import { cn } from '@/lib/utils/cn';
import {
  newIdempotencyKey,
  previewLibraryLoad,
  useLoadLibraryMutation,
  type EngagementRequirement,
  type LoadResult,
} from '@/lib/api/engagement-requirements';
import type { LibraryItem, LibraryTree } from '@/lib/api/requirement-library';
import {
  engagementLibraryState,
  pickState,
  pickSummary,
  plural,
  togglePick,
} from './requirements-logic';

/**
 * "Add from library" sheet (T-0143; E11-F05).
 *
 * Reference: `openPicker` / `renderPicker` / `pickSummary` in
 * `docs/requirement-library/prototype/selection.js`. Left: categories and
 * modules with tri-state checkboxes and "selected/total" counts. Right:
 * the viewed module's requirements grouped by sub-category, or the
 * search results across the library. Requirements already in the
 * engagement are ticked and locked; ones removed earlier are tagged and
 * come back only if "bring back" stays ticked.
 *
 * The preview is the footer of this same surface, not a second dialog:
 * to add / removed earlier / already in this engagement, in the groups
 * the server's `:load-preview` uses. The groups are computed here from
 * the engagement's rows (identity is the library item id, never the
 * wording), so ticking a box costs no request. Once the selection has
 * settled, one debounced `:load-preview` checks it against the server;
 * if they disagree (someone else changed the engagement) the rows are
 * refetched and the totals follow. The `:load` response is the source of
 * truth for what was added.
 *
 * A reason is required (Decision 34): one per load, cited by every
 * requirement in it. The load carries an `Idempotency-Key` that is
 * reused if the same confirm is retried, so it happens once.
 */

const SEARCH_LIMIT = 300;
const PREVIEW_DEBOUNCE_MS = 600;

export interface LibraryPickerSheetProps {
  engagementId: string;
  /** "Client · Engagement", for the sheet description. */
  engagementLabel: string | null;
  tree: LibraryTree;
  items: ReadonlyArray<LibraryItem>;
  /** Every requirement of the engagement, removed ones included. */
  rows: ReadonlyArray<EngagementRequirement>;
  onClose: () => void;
  onLoaded: (result: LoadResult) => void;
  /** The server's preview disagreed with the rows held here. */
  onStale: () => void;
}

export function LibraryPickerSheet({
  engagementId,
  engagementLabel,
  tree,
  items,
  rows,
  onClose,
  onLoaded,
  onStale,
}: LibraryPickerSheetProps) {
  const reasonId = React.useId();
  const [query, setQuery] = React.useState('');
  const [picked, setPicked] = React.useState<ReadonlySet<string>>(() => new Set());
  const [touched, setTouched] = React.useState<ReadonlySet<string>>(() => new Set());
  const [restoreRemoved, setRestoreRemoved] = React.useState(true);
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const load = useLoadLibraryMutation(engagementId);

  const modules = React.useMemo(
    () =>
      tree.categories.flatMap((c) =>
        c.modules.filter((m) => m.current_version > 0).map((m) => ({ ...m, category_id: c.id })),
      ),
    [tree],
  );
  const [viewModuleId, setViewModuleId] = React.useState<string | null>(null);
  const viewModule = modules.find((m) => m.id === viewModuleId) ?? modules[0] ?? null;

  const state = React.useMemo(() => engagementLibraryState(rows), [rows]);
  const terms = React.useMemo(() => searchTerms(query), [query]);
  const searching = terms.length > 0;

  const matching = React.useMemo(
    () => (searching ? items.filter((i) => matchesSearch(i, terms)) : items),
    [items, terms, searching],
  );
  const byModule = React.useMemo(() => {
    const map = new Map<string, LibraryItem[]>();
    for (const i of matching) {
      const bucket = map.get(i.module_id);
      if (bucket) bucket.push(i);
      else map.set(i.module_id, [i]);
    }
    return map;
  }, [matching]);

  const shown = React.useMemo(
    () =>
      searching
        ? matching.slice(0, SEARCH_LIMIT)
        : viewModule
          ? (byModule.get(viewModule.id) ?? [])
          : [],
    [searching, matching, viewModule, byModule],
  );
  const groups = React.useMemo(() => {
    const map = new Map<string, LibraryItem[]>();
    for (const i of shown) {
      const key = `${i.module_id}\u0000${i.subcategory}`;
      const bucket = map.get(key);
      if (bucket) bucket.push(i);
      else map.set(key, [i]);
    }
    return Array.from(map.values());
  }, [shown]);

  const summary = React.useMemo(
    () => pickSummary(items, picked, touched, state, restoreRemoved),
    [items, picked, touched, state, restoreRemoved],
  );

  // What the load sends: everything the consultant ticked, including what
  // the engagement already holds, so the audit entry records the selection
  // and the server classifies it for itself.
  const selectionIds = React.useMemo(
    () => [...summary.toAdd, ...summary.removedEarlier, ...summary.alreadyPresent],
    [summary],
  );

  // One key per distinct load; a retry of the same confirm reuses it.
  const idempotency = React.useRef<{ signature: string; key: string } | null>(null);

  // Check the settled selection against the server, quietly.
  const onStaleRef = React.useRef(onStale);
  onStaleRef.current = onStale;
  React.useEffect(() => {
    if (selectionIds.length === 0) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      previewLibraryLoad(engagementId, selectionIds, controller.signal).then(
        (preview) => {
          if (
            preview.counts.to_add !== summary.toAdd.length ||
            preview.counts.removed_earlier !== summary.removedEarlier.length ||
            preview.counts.already_present !== summary.alreadyPresent.length
          ) {
            onStaleRef.current();
          }
        },
        // The footer still shows the local totals; `:load` decides anyway.
        () => undefined,
      );
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [engagementId, selectionIds, summary]);

  const toggle = (subset: ReadonlyArray<LibraryItem>, moduleIds: ReadonlyArray<string>) => {
    const wasAllOn = pickState(subset, state.present, picked) === 'on';
    setPicked((prev) => togglePick(prev, subset, state.present));
    if (moduleIds.length > 0) {
      setTouched((prev) => {
        const next = new Set(prev);
        for (const id of moduleIds) {
          if (wasAllOn) next.delete(id);
          else next.add(id);
        }
        return next;
      });
    }
    setError(null);
  };

  const touch = (moduleIds: ReadonlyArray<string>) =>
    setTouched((prev) => {
      const next = new Set(prev);
      moduleIds.forEach((id) => next.add(id));
      return next;
    });

  const confirm = () => {
    const trimmed = reason.trim();
    if (summary.total === 0 || !trimmed || load.isPending) return;
    const signature = JSON.stringify([selectionIds, restoreRemoved, trimmed]);
    if (idempotency.current?.signature !== signature) {
      idempotency.current = { signature, key: newIdempotencyKey() };
    }
    setError(null);
    load.mutate(
      {
        item_ids: selectionIds,
        restore_removed: restoreRemoved,
        reason: trimmed,
        idempotencyKey: idempotency.current.key,
      },
      {
        onSuccess: onLoaded,
        onError: (err) =>
          setError(
            err instanceof Error && err.message
              ? err.message
              : 'The requirements could not be added. Nothing was changed; try again.',
          ),
      },
    );
  };

  const canConfirm = summary.total > 0 && reason.trim().length > 0 && !load.isPending;
  const shownOpen = shown.filter((i) => !state.present.has(i.item_id));
  const shownAllOn = shownOpen.length > 0 && shownOpen.every((i) => picked.has(i.item_id));

  return (
    <Sheet
      open
      onOpenChange={(next) => {
        if (!next && !load.isPending) onClose();
      }}
      closeOnScrimClick={picked.size === 0 && reason.length === 0}
      title="Add from library"
      description={`${engagementLabel ? `Into ${engagementLabel}. ` : ''}Tick whole categories, modules, sub-categories or single requirements.`}
      headerActions={
        <SearchField
          value={query}
          onChange={(value) => {
            setQuery(value);
            listRef.current?.scrollTo({ top: 0 });
          }}
          aria-label="Search the library"
          placeholder="Search the library…"
          wrapperClassName="w-[300px]"
          icon={<Search className="h-3.5 w-3.5 flex-none" aria-hidden="true" />}
        />
      }
      summary={
        <>
          <SheetStat value={summary.toAdd.length.toLocaleString()} label="to add" />
          {summary.removedEarlier.length > 0 ? (
            <span className="inline-flex items-center gap-1.5">
              <TriCheckbox
                state={restoreRemoved ? 'on' : 'off'}
                aria-label="Bring back the requirements removed earlier"
                onChange={(next) => setRestoreRemoved(next)}
              />
              <SheetStat
                value={summary.removedEarlier.length.toLocaleString()}
                label="removed earlier — bring back"
              />
            </span>
          ) : null}
          <SheetStat
            value={summary.alreadyPresent.length.toLocaleString()}
            label="already in this engagement"
          />
        </>
      }
      actions={
        <>
          <label htmlFor={reasonId} className="sr-only">
            Why these are being added (required)
          </label>
          <input
            id={reasonId}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirm();
            }}
            maxLength={2000}
            autoComplete="off"
            placeholder="Why these are being added (required), e.g. scope agreed at kickoff"
            className="h-9 w-[340px] max-w-[32vw] rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-accent-amber-600"
          />
          <WorkspaceButton size="md" disabled={load.isPending} onClick={onClose}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            variant="primary"
            disabled={!canConfirm}
            title={
              summary.total > 0 && !reason.trim() ? 'Give a reason for this load first' : undefined
            }
            onClick={confirm}
          >
            {load.isPending
              ? `Adding ${plural(summary.total, 'requirement')}…`
              : summary.total > 0
                ? `Add ${plural(summary.total, 'requirement')}`
                : 'Nothing new to add'}
          </WorkspaceButton>
        </>
      }
    >
      <div
        role="group"
        aria-label="Library categories and modules"
        className="w-[320px] flex-none overflow-y-auto border-r border-ink-100 bg-paper-50 p-2 max-[1100px]:w-[260px]"
      >
        {tree.categories.map((cat) => {
          const catModules = modules.filter((m) => m.category_id === cat.id && byModule.has(m.id));
          if (catModules.length === 0) return null;
          const catItems = catModules.flatMap((m) => byModule.get(m.id) ?? []);
          const catState = pickState(catItems, state.present, picked);
          const catIds = catModules.map((m) => m.id);
          return (
            <div key={cat.id}>
              <div className="mt-1.5 flex h-8 items-center gap-2 rounded-chip px-2 text-body-sm font-semibold text-ink-900">
                <span
                  className="inline-flex"
                  onClickCapture={catState === 'locked' ? () => touch(catIds) : undefined}
                >
                  <TriCheckbox
                    state={catState}
                    aria-label={`Select all of ${cat.name}`}
                    onChange={() => toggle(catItems, catIds)}
                  />
                </span>
                <span className="min-w-0 flex-1 truncate">{cat.name}</span>
                <span className="font-mono text-[11px] font-normal text-ink-500">
                  {catItems.length.toLocaleString()}
                </span>
              </div>
              {catModules.map((m) => {
                const modItems = byModule.get(m.id) ?? [];
                const modState = pickState(modItems, state.present, picked);
                const selected = modItems.filter((i) => picked.has(i.item_id)).length;
                const viewing = !searching && viewModule?.id === m.id;
                return (
                  <div
                    key={m.id}
                    data-module-row={m.id}
                    className={cn(
                      'flex h-8 items-center gap-2 rounded-chip pl-7 pr-2 text-body-sm',
                      viewing
                        ? 'bg-paper-0 font-semibold text-ink-900 shadow-[inset_0_0_0_1px_var(--ink-100)]'
                        : 'text-ink-700 hover:bg-paper-100',
                    )}
                  >
                    <span
                      className="inline-flex"
                      onClickCapture={modState === 'locked' ? () => touch([m.id]) : undefined}
                    >
                      <TriCheckbox
                        state={modState}
                        aria-label={`Select all of ${m.name}`}
                        onChange={() => {
                          toggle(modItems, [m.id]);
                          setViewModuleId(m.id);
                        }}
                      />
                    </span>
                    <button
                      type="button"
                      aria-current={viewing ? 'true' : undefined}
                      onClick={() => {
                        setViewModuleId(m.id);
                        if (query) setQuery('');
                        listRef.current?.scrollTo({ top: 0 });
                      }}
                      className="min-w-0 flex-1 truncate rounded-chip text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
                    >
                      {m.name}
                    </button>
                    <span
                      className="font-mono text-[11px] font-normal text-ink-500"
                      aria-label={`${selected} of ${modItems.length} selected`}
                    >
                      {selected > 0 ? `${selected}/` : ''}
                      {modItems.length}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
        {byModule.size === 0 ? (
          <p className="px-3 py-8 text-center text-body-sm text-ink-500">
            {searching ? 'No modules match.' : 'The library has no published requirements yet.'}
          </p>
        ) : null}
      </div>

      <div ref={listRef} className="min-w-0 flex-1 overflow-y-auto">
        <div className="sticky top-0 z-[2] flex items-center gap-2.5 border-b border-ink-100 bg-paper-0 px-5 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-h3 font-semibold text-ink-900">
              {searching
                ? `${plural(matching.length, 'match', 'matches')} for "${query.trim()}"`
                : (viewModule?.name ?? 'Library')}
            </div>
            <div className="text-meta font-medium text-ink-500">
              {searching
                ? matching.length > SEARCH_LIMIT
                  ? `Showing the first ${SEARCH_LIMIT}. Refine the search to narrow down.`
                  : 'Across all modules'
                : viewModule
                  ? `Version ${viewModule.current_version} · ${plural(shown.length, 'requirement')}`
                  : ''}
            </div>
          </div>
          {shownOpen.length > 0 ? (
            <WorkspaceButton
              onClick={() => toggle(shown, searching ? [] : viewModule ? [viewModule.id] : [])}
            >
              {shownAllOn ? 'Clear these' : 'Select these'}
            </WorkspaceButton>
          ) : null}
        </div>

        {error ? (
          <div className="px-5 pt-3">
            <WorkspaceNotice tone="critical">{error}</WorkspaceNotice>
          </div>
        ) : null}

        {groups.map((group) => {
          const first = group[0]!;
          const groupState = pickState(group, state.present, picked);
          return (
            <div key={`${first.module_id}\u0000${first.subcategory}`}>
              <div className="flex items-center gap-2.5 border-b border-ink-100 bg-paper-50 px-5 py-2 text-meta font-semibold text-ink-700">
                <TriCheckbox
                  state={groupState}
                  aria-label={`Select all of ${first.subcategory}`}
                  onChange={() => toggle(group, [])}
                />
                {searching ? (
                  <span className="font-medium text-ink-500">{first.module_name} ›</span>
                ) : null}
                <span className="truncate">{first.subcategory}</span>
                <span className="font-mono font-normal text-ink-500">{group.length}</span>
              </div>
              {group.map((item) => {
                const locked = state.present.has(item.item_id);
                const on = picked.has(item.item_id);
                const removedEarlier = !locked && state.removed.has(item.item_id);
                return (
                  <div
                    key={item.item_id}
                    data-item-id={item.item_id}
                    onClick={locked ? undefined : () => toggle([item], [])}
                    className={cn(
                      'grid grid-cols-[16px_84px_minmax(0,1fr)_auto] items-start gap-3 border-b border-ink-100 px-5 py-[9px] text-body-sm leading-5',
                      locked ? 'cursor-default' : 'cursor-pointer hover:bg-paper-50',
                    )}
                  >
                    <TriCheckbox
                      state={locked ? 'locked' : on ? 'on' : 'off'}
                      aria-label={
                        locked
                          ? `${item.number} is already in this engagement`
                          : `Select ${item.number}`
                      }
                      className="mt-0.5"
                      onChange={() => toggle([item], [])}
                    />
                    <span
                      className={cn(
                        'font-mono text-meta font-medium leading-5',
                        locked ? 'text-ink-500' : 'text-ink-700',
                      )}
                    >
                      <Highlight text={item.number} terms={terms} />
                    </span>
                    <span className={locked ? 'text-ink-500' : 'text-ink-900'}>
                      <Highlight text={item.text} terms={terms} />
                    </span>
                    <span className="flex items-center gap-1.5">
                      {locked ? (
                        <span className="inline-flex h-[22px] items-center whitespace-nowrap rounded-full bg-ink-100 px-2.5 text-meta font-medium text-ink-700">
                          In engagement
                        </span>
                      ) : removedEarlier ? (
                        <span className="inline-flex h-[22px] items-center whitespace-nowrap rounded-full bg-posture-at-risk-tint px-2.5 text-meta font-medium text-posture-at-risk">
                          Removed earlier
                        </span>
                      ) : null}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
        {shown.length === 0 && byModule.size > 0 ? (
          <p className="px-6 py-14 text-center text-body-sm text-ink-500">
            Nothing matches this search.
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}
